import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef,
  NgZone, OnChanges, OnDestroy, SimpleChanges,
  input,
  viewChild,
  output
} from '@angular/core';

/**
 * Monaco-based JSON editor for the Playground's request body — a drop-in replacement for
 * the plain ion-textarea, adding schema-driven autocomplete/validation for whichever
 * endpoint is currently selected (docs/KONZEPT-Developer-Platform.md).
 *
 * Monaco is lazy-loaded via its own AMD loader (assets/monaco/vs/loader.js, copied out of
 * node_modules/monaco-editor/min/vs by angular.json's assets array) rather than bundled
 * through Angular's build pipeline: monaco-editor's official webpack plugin doesn't apply
 * to @angular-devkit/build-angular:application (esbuild-based), and hand-rolling worker
 * bundling for esbuild is exactly the kind of pain this sidesteps entirely. The loader
 * script is injected at most once app-wide (static promise below), even if multiple
 * instances of this component exist (e.g. a future side-by-side diff view).
 */
@Component({
  selector: 'app-json-editor',
  standalone: true,
  templateUrl: './json-editor.component.html',
  styleUrls: ['./json-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly value = input('');
  readonly valueChange = output<string>();

  /** Resolved JSON schema for the currently selected endpoint's request body, or null to
   * disable schema validation/autocomplete (see openapi-schema.util.ts). */
  readonly jsonSchema = input<object | null>(null);

  private readonly containerRef = viewChild.required<ElementRef<HTMLDivElement>>('editorContainer');

  private static monacoReadyPromise: Promise<any> | null = null;
  private static schemaUriCounter = 0;

  private editor: any = null;
  private monaco: any = null;

  /** jsonSchema arrives via ngOnChanges before ngAfterViewInit has resolved Monaco (or
   * even run) in the common case where the parent sets [jsonSchema] on first render —
   * stash it and apply once Monaco is actually ready. */
  private pendingSchema: object | null | undefined;
  private hasPendingSchema = false;

  constructor(private zone: NgZone) { }

  ngAfterViewInit(): void {
    JsonEditorComponent.loadMonaco().then(monaco => {
      this.monaco = monaco;
      this.createEditor();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.editor) {
      const incoming = this.value() ?? '';
      // Guard against clobbering cursor position / re-triggering ourselves when the
      // change originated from the editor's own onDidChangeModelContent below.
      if (this.editor.getValue() !== incoming) {
        this.editor.setValue(incoming);
      }
    }

    if (changes['jsonSchema']) {
      if (this.monaco) {
        this.applySchema(this.jsonSchema());
      } else {
        this.pendingSchema = this.jsonSchema();
        this.hasPendingSchema = true;
      }
    }
  }

  ngOnDestroy(): void {
    this.editor?.dispose();
    this.editor = null;
  }

  private createEditor(): void {
    this.editor = this.monaco.editor.create(this.containerRef().nativeElement, {
      value: this.value() ?? '',
      language: 'json',
      automaticLayout: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      // Monaco's touch/mobile support has real limits (mouse-sized scrollbars, no gesture
      // handling) that this one option can't fix — accepted for a tool used at a desk — but
      // wordWrap at least avoids forcing horizontal panning inside the editor's own tiny
      // viewport for long JSON lines, which is the one part of that gap worth closing.
      wordWrap: 'on',
      tabSize: 2,
      fontSize: 13,
      fontFamily: 'var(--font-mono)',
    });

    this.editor.onDidChangeModelContent(() => {
      const current = this.editor.getValue();
      // onDidChangeModelContent fires outside Angular's zone (Monaco schedules its own
      // internals) — re-enter so the parent's [(value)] binding + change detection see it.
      this.zone.run(() => this.valueChange.emit(current));
    });

    const jsonSchema = this.jsonSchema();
    if (this.hasPendingSchema) {
      this.applySchema(this.pendingSchema);
      this.hasPendingSchema = false;
    } else if (jsonSchema) {
      this.applySchema(jsonSchema);
    }
  }

  private applySchema(schema: object | null | undefined): void {
    if (!this.monaco) return;

    // A fresh uri per call — Monaco keys its schema cache by uri, so reusing one across
    // different endpoint selections would serve stale validation/autocomplete.
    const uri = `inmemory://model/schema-${++JsonEditorComponent.schemaUriCounter}.json`;
    this.monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      enableSchemaRequest: false,
      schemas: schema ? [{ uri, fileMatch: ['*'], schema }] : [],
    });
  }

  private static loadMonaco(): Promise<any> {
    if ((window as any).monaco) {
      return Promise.resolve((window as any).monaco);
    }
    if (JsonEditorComponent.monacoReadyPromise) {
      return JsonEditorComponent.monacoReadyPromise;
    }

    JsonEditorComponent.monacoReadyPromise = new Promise((resolve, reject) => {
      const onLoaderReady = () => {
        (window as any).require.config({ paths: { vs: 'assets/monaco/vs' } });
        (window as any).require(['vs/editor/editor.main'], () => resolve((window as any).monaco));
      };

      const existingScript = document.getElementById('monaco-amd-loader') as HTMLScriptElement | null;
      if (existingScript) {
        // Another instance already injected the loader script — if its require() global
        // isn't there yet, the script is still loading, so wait for its load event too.
        if ((window as any).require) onLoaderReady();
        else existingScript.addEventListener('load', onLoaderReady, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = 'monaco-amd-loader';
      // Relative (no leading slash): this app is statically hosted and must resolve
      // correctly under its deployed base href (see index.html's <base href="/">),
      // exactly like the translations loader's 'assets/translations/' prefix in
      // app.module.ts — never hardcode an absolute "/assets/..." path here.
      script.src = 'assets/monaco/vs/loader.js';
      script.onload = onLoaderReady;
      script.onerror = reject;
      document.body.appendChild(script);
    });

    return JsonEditorComponent.monacoReadyPromise;
  }
}
